import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Avatar,
  Typography,
  Box,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import { Reply, Comment as CommentIcon, ExpandMore as ExpandMoreIcon, Person, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { Post, User, Comment } from '../types/forum.types';
import { FileDisplay } from './FileDisplay';
import { useMarkdown } from '../hooks/useMarkdown';
import ReplyForm from './ReplyForm';

interface PostCardProps {
  initialPost: Post;
  userInfo: User | undefined;
  categoryName: string;
  comments: Comment[];
  isExpanded: boolean;
  loadedImages: { [key: string]: string };
  onExpandThread: (postId: string, forceRefresh?: boolean) => Promise<void>;
  allUsers?: User[];
  currentUserId?: number;
  onEditPost?: (post: Post) => void;
  onDeletePost?: (post: Post) => void;
}

export const PostCard: React.FC<PostCardProps> = ({
  initialPost,
  userInfo,
  categoryName,
  comments,
  isExpanded,
  loadedImages,
  onExpandThread,
  allUsers = [],
  currentUserId,
  onEditPost,
  onDeletePost,
}) => {
  const { renderMarkdown } = useMarkdown();
  const [post, setPost] = useState(initialPost);
  const theme = useTheme();
  const isDarkTheme = theme.name === 'dark';

  const getCommentAuthorInfo = (authorId: number) => {
    return allUsers.find(user => user.user_id === authorId);
  };

  const handleReplySuccess = () => {
    setPost(prevPost => ({
      ...prevPost,
      comments_count: prevPost.comments_count + 1
    }));
    
    if (isExpanded) {
      onExpandThread(post.id, true);
    } else {
      onExpandThread(post.id);
    }
  };

  // Check if the current user is the author of the post
  const isAuthor = currentUserId && post.author_id === currentUserId;

  return (
    <Card 
      elevation={isDarkTheme ? 3 : 1}
      sx={{ 
        mb: 2, 
        borderRadius: isDarkTheme ? '8px' : '0px',
        bgcolor: isDarkTheme ? alpha(theme.palette.background.paper, 0.6) : 'background.paper',
        borderLeft: '4px solid',
        borderColor: 'primary.main',
        backdropFilter: 'blur(10px)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: isDarkTheme ? '0 8px 16px rgba(0, 0, 0, 0.3)' : '0 4px 8px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <CardHeader
        avatar={
          <Avatar 
            src={userInfo?.profile_image_path} 
            sx={{ 
              width: 40, 
              height: 40,
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: isDarkTheme ? '8px' : '0px',
              boxShadow: isDarkTheme ? '0 2px 4px rgba(0, 0, 0, 0.2)' : 'none'
            }}
          >
            {userInfo?.username ? userInfo.username.charAt(0).toUpperCase() : <Person />}
          </Avatar>
        }
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ 
              color: 'text.primary',
              fontWeight: 'bold',
              textShadow: isDarkTheme ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none'
            }}>
              {post.title}
            </Typography>
            <Box sx={{ display: 'flex' }}>
              {isAuthor && onEditPost && (
                <Tooltip title="Edit post" placement="top">
                  <IconButton 
                    onClick={() => onEditPost(post)} 
                    size="small"
                    sx={{ 
                      color: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
                      '&:hover': {
                        bgcolor: isDarkTheme ? alpha('#64AFE3', 0.1) : alpha(theme.palette.primary.main, 0.1)
                      }
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {isAuthor && onDeletePost && (
                <Tooltip title="Delete post" placement="top">
                  <IconButton 
                    onClick={() => onDeletePost(post)} 
                    size="small"
                    sx={{ 
                      color: theme.palette.error.main,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.error.main, 0.1)
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        }
        subheader={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {userInfo?.username || `User ${post.author_id}`} • {formatDistanceToNow(new Date(post.created_at))} ago
            </Typography>
            <Chip
              label={categoryName}
              size="small"
              sx={{ 
                bgcolor: isDarkTheme ? '#64AFE3' : 'primary.main',
                color: isDarkTheme ? '#000000' : 'background.paper',
                borderRadius: isDarkTheme ? '4px' : '0px',
                fontWeight: 'medium',
                boxShadow: isDarkTheme ? '0 2px 4px rgba(0, 0, 0, 0.2)' : 'none'
              }}
            />
          </Box>
        }
      />
      
      <CardContent>
        {post.tags && post.tags.length > 0 && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {post.tags.map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: '4px',
                  borderColor: isDarkTheme ? '#64AFE3' : 'primary.main',
                  color: isDarkTheme ? '#64AFE3' : 'primary.main',
                  '&:hover': {
                    backgroundColor: isDarkTheme ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ 
          borderRadius: isDarkTheme ? '4px' : '0px',
          p: 2, 
          bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.5) : alpha(theme.palette.background.secondary, 0.3),
          mb: 2,
          '& a': {
            color: isDarkTheme ? '#64AFE3' : 'primary.main',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          },
          '& img': {
            maxWidth: '100%',
            borderRadius: '4px'
          },
          '& pre': {
            backgroundColor: isDarkTheme ? alpha('#000', 0.5) : alpha('#f5f5f5', 0.8),
            padding: theme.spacing(1.5),
            borderRadius: '4px',
            overflowX: 'auto'
          },
          '& code': {
            backgroundColor: isDarkTheme ? alpha('#000', 0.3) : alpha('#f5f5f5', 0.5),
            padding: '0.2em 0.4em',
            borderRadius: '3px',
            fontSize: '0.85em',
            fontFamily: 'monospace'
          },
          '& blockquote': {
            borderLeft: `4px solid ${isDarkTheme ? '#64AFE3' : theme.palette.primary.main}`,
            margin: '1em 0',
            padding: '0.5em 1em',
            backgroundColor: isDarkTheme ? alpha('#000', 0.2) : alpha('#f5f5f5', 0.3)
          }
        }}>
          {renderMarkdown(post.content)}
        </Box>

        <FileDisplay 
          files={post.files}
          postId={post.id}
          loadedImages={loadedImages}
        />
        
        <Box sx={{ 
          mt: 2, 
          pt: 2,
          borderTop: '1px solid',
          borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.5) : 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
          {post.comments_count > 0 && (
            <Box 
              onClick={(e) => {
                e.stopPropagation();
                onExpandThread(post.id);
              }}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                py: 1,
                borderRadius: isDarkTheme ? '4px' : '0px',
                bgcolor: isDarkTheme ? theme.palette.background.secondary : 'background.secondary',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                border: '1px solid',
                borderColor: isDarkTheme ? '#64AFE3' : 'primary.main',
                color: isDarkTheme ? '#64AFE3' : 'primary.main',
                width: '100%',
                '&:hover': {
                  bgcolor: isDarkTheme ? '#64AFE3' : 'primary.main',
                  color: isDarkTheme ? '#000000' : 'primary.contrastText',
                  transform: 'translateY(-1px)',
                  boxShadow: isDarkTheme ? '0 4px 8px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              <CommentIcon sx={{ fontSize: '1.2rem' }} />
              <Typography variant="button">
                {isExpanded ? 'Hide' : 'Show'} {post.comments_count} {post.comments_count === 1 ? 'reply' : 'replies'}
              </Typography>
              <ExpandMoreIcon 
                sx={{ 
                  transition: 'transform 0.2s ease-in-out',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }} 
              />
            </Box>
          )}

          <ReplyForm 
            postId={post.id}
            onReplySuccess={handleReplySuccess}
          />

          <Collapse in={isExpanded} timeout={300}>
            {comments.length > 0 ? (
              <Box 
                sx={{ 
                  mt: 2, 
                  p: 2, 
                  borderRadius: isDarkTheme ? '8px' : '0px',
                  bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : alpha(theme.palette.background.secondary, 0.2),
                  border: '1px solid',
                  borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.2) : 'divider'
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2, color: isDarkTheme ? alpha(theme.palette.text.primary, 0.7) : 'text.secondary' }}>
                  Replies ({comments.length})
                </Typography>
                
                <Box sx={{ 
                  maxHeight: comments.length > 4 ? '400px' : 'none',
                  overflowY: comments.length > 4 ? 'auto' : 'visible',
                  pr: comments.length > 4 ? 1 : 0,
                  mr: comments.length > 4 ? -1 : 0,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: isDarkTheme ? alpha(theme.palette.background.paper, 0.1) : alpha(theme.palette.background.paper, 0.3),
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: isDarkTheme ? alpha('#64AFE3', 0.5) : alpha(theme.palette.primary.main, 0.3),
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: isDarkTheme ? '#64AFE3' : theme.palette.primary.main,
                    },
                  },
                }}>
                  {comments.map((comment, index) => {
                    const commentAuthor = getCommentAuthorInfo(comment.author_id);
                    
                    return (
                      <Box key={comment.id} sx={{ 
                        mb: index !== comments.length - 1 ? 2 : 0,
                        borderBottom: index !== comments.length - 1 ? '1px solid' : 'none',
                        borderColor: isDarkTheme ? alpha(theme.palette.divider, 0.1) : 'divider',
                        pb: index !== comments.length - 1 ? 2 : 0
                      }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <Avatar 
                            src={commentAuthor?.profile_image_path}
                            sx={{ 
                              width: 32, 
                              height: 32,
                              bgcolor: theme.palette.primary.main,
                              color: theme.palette.primary.contrastText,
                              borderRadius: isDarkTheme ? '6px' : '0px'
                            }}
                          >
                            {commentAuthor?.username ? commentAuthor.username.charAt(0).toUpperCase() : <Person />}
                          </Avatar>
                          
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {commentAuthor?.username || `User ${comment.author_id}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formatDistanceToNow(new Date(comment.created_at))} ago
                              </Typography>
                            </Box>
                            
                            <Typography variant="body2" sx={{ 
                              color: isDarkTheme ? alpha(theme.palette.text.primary, 0.95) : 'text.primary',
                              wordBreak: 'break-word'
                            }}>
                              {comment.content}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ) : (
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                borderRadius: isDarkTheme ? '8px' : '0px', 
                bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : alpha(theme.palette.background.secondary, 0.2),
                textAlign: 'center'
              }}>
                <Typography variant="body2" color="text.secondary">
                  No replies yet. Be the first to reply!
                </Typography>
              </Box>
            )}
          </Collapse>
        </Box>
      </CardContent>
    </Card>
  );
}; 